import { Box, Grid, Typography } from "@mui/material";
import { Space } from "~/lib/types";
import { useTicketsPage } from "~/pages/tickets/hooks/useTicketsPage";
import { ScrollToTop } from "~/lib/scroll-to-top";
import { TicketsTabs } from "~/pages/tickets/tickets/TicketsPage/TicketsTabs/TicketsTabs";
import { CASES_TABS } from "~/pages/tickets/constants";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useSearchParams } from "react-router-dom";
import { ASSET_ID_PARAM, getAssetMrn } from "~/lib/urlUtils";
import { KeyValueChip } from "~/components/key-value-chip";
import { useLoadAssetQuery } from "~/operations";
import { ClosedTickets } from "~/pages/tickets/tickets/TicketsPage/ClosedTickets/ClosedTickets";
import { OpenTickets } from "~/pages/tickets/tickets/TicketsPage/OpenTickets/OpenTickets";
import { TicketsCreateFirstTicket } from "~/pages/tickets/tickets/TicketsPage/TicketsCreateFirstTickets/TicketsCreateFirstTicket";
import { CreateTicketingIntegration } from "~/pages/tickets/components/CreateTicketingIntegration/CreateTicketingIntegration";
import { TicketsTitle } from "~/pages/tickets/tickets/TicketsPage/TicketsTitle/TicketsTitle";
import Breadcrumbs, { Breadcrumb } from "~/components/Breadcrumbs/Breadcrumbs";
import { setDocumentTitle } from "~/lib/commonUtils";

const breadcrumbs: Breadcrumb[] = [{ title: "Ticketing", key: "tickets" }];

type CasesPageProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
};

export const TicketsPage = ({ space, scope }: CasesPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const assetId = searchParams.get(ASSET_ID_PARAM);
  const assetMrn = assetId ? getAssetMrn(space.id, assetId) : undefined;

  const { data: assetData } = useLoadAssetQuery({
    variables: { mrn: assetMrn || "skip" },
    skip: !assetMrn,
  });

  const handleClearAssetFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete(ASSET_ID_PARAM);
    setSearchParams(params);
  };

  const {
    openCases,
    openCasesDataLoading,
    closedCases,
    closedCasesDataLoading,
    showCreateFirstCaseScreen,
    showCreateTicketingIntegrationScreen,
    hasCases,
    tabList,
    currentTab,
    onTabChange,
    openCasesPaginateProps,
    closedCasesPaginateProps,
  } = useTicketsPage({
    space,
    scope,
    assetId,
  });

  setDocumentTitle(["Ticketing", scope.name]);

  return (
    <Box>
      <ScrollToTop />
      <Grid container>
        <Breadcrumbs items={breadcrumbs} />
        <TicketsTitle />
        {/* No API Support and no needed for first iteration */}
        {/*<TicketsFilterSection />*/}

        {!showCreateFirstCaseScreen &&
          !showCreateTicketingIntegrationScreen && (
            <Grid size={12}>
              <TicketsTabs
                tabs={tabList}
                onTabChange={onTabChange}
                currentTab={currentTab}
              />
            </Grid>
          )}
        {showCreateFirstCaseScreen && <TicketsCreateFirstTicket />}

        {hasCases && (
          <>
            {assetId && (
              <Grid
                sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
                size={12}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ lineHeight: 1, marginTop: 1 }}
                >
                  Filtered By:
                </Typography>
                <KeyValueChip
                  keyValue={{
                    key: "Asset",
                    value: assetData?.asset?.name || assetId,
                  }}
                  onDelete={handleClearAssetFilter}
                />
              </Grid>
            )}
            {currentTab === CASES_TABS.OPEN ? (
              <OpenTickets
                openCases={openCases}
                isLoading={openCasesDataLoading}
                scope={scope}
                paginateProps={openCasesPaginateProps}
              />
            ) : (
              <ClosedTickets
                closedCases={closedCases}
                isLoading={closedCasesDataLoading}
                scope={scope}
                paginateProps={closedCasesPaginateProps}
              />
            )}
          </>
        )}

        {showCreateTicketingIntegrationScreen && (
          <CreateTicketingIntegration scope={scope} />
        )}
      </Grid>
    </Box>
  );
};
